/**
 * Section Library — Advanced Section Manager
 *
 * Features:
 * - Tab navigation: Templates / Current Sections / Favorites
 * - Generic section templates for all page types (hero, content, CTA, etc.)
 * - About page pre-designed sections from aboutSectionBlocks
 * - Search with category filtering
 * - Favorites system (persisted to localStorage)
 * - Recently used sections
 * - Section preview mini cards
 * - One-click add with toast feedback
 * - Drag handle for future drag-to-position
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  X, Layout, Image, Type, ShoppingBag, Star,
  MessageCircle, Mail, HelpCircle, Layers, Search, Plus,
  Users, BarChart3, Clock, Heart, Camera, Quote, Megaphone, Compass, Phone,
  Bookmark, BookmarkCheck, Zap, Gift, Sparkles, Grid, ArrowRight,
  Mountain, Trophy, Target, MessageSquare, Newspaper, CheckCircle
} from 'lucide-react';
import aboutSectionBlocks from './aboutSectionBlocks';

// ── Category definitions ─────────────────────────────────────────────
const SECTION_CATEGORIES = {
  'Navigation': { icon: Layout, match: ['navbar','header','nav','menu'] },
  'Hero': { icon: Image, match: ['hero','banner','slider','jumbotron'] },
  'Content': { icon: Type, match: ['about','story','content','text','features','intro'] },
  'Products': { icon: ShoppingBag, match: ['product','collection','shop','category','catalog','trending'] },
  'Testimonials': { icon: Star, match: ['testimonial','review','rating'] },
  'Contact': { icon: Mail, match: ['contact','newsletter','subscribe','email'] },
  'FAQ': { icon: HelpCircle, match: ['faq','question','help'] },
  'Social': { icon: MessageCircle, match: ['social','instagram','gallery'] },
  'CTA': { icon: Megaphone, match: ['cta','call-to-action','promo','banner-cta'] },
  'Stats': { icon: BarChart3, match: ['stats','numbers','counter','metric'] },
  'Team': { icon: Users, match: ['team','member','staff','founder'] },
  'Other': { icon: Layers, match: [] }
};

const ABOUT_CATEGORY_ICONS = {
  'Hero': Image, 'Story': Type, 'Team': Users, 'Stats': BarChart3,
  'Timeline': Clock, 'Values': Heart, 'Gallery': Camera,
  'Content': Quote, 'CTA': Megaphone, 'Process': Compass, 'Contact': Phone,
};

// ── Generic Section Templates (works for any page type) ──────────────
const GENERIC_SECTION_TEMPLATES = [
  {
    id: 'hero-centered',
    category: 'Hero',
    label: 'Centered Hero',
    description: 'Full-width hero with centered text and CTA button',
    icon: Mountain,
    block: {
      id: 'hero-centered',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        className: 'relative min-h-[80vh] flex items-center justify-center bg-gray-900 text-white',
        style: { minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#111827', color: '#ffffff', textAlign: 'center', padding: '80px 24px' },
        children: [
          {
            type: 'div',
            style: { maxWidth: '700px', margin: '0 auto' },
            children: [
              { type: 'h1', content: 'Your Brand Story', style: { fontSize: '56px', fontWeight: '700', lineHeight: '1.1', marginBottom: '20px', letterSpacing: '-0.03em' } },
              { type: 'p', content: 'Crafting timeless fashion that speaks to who you are. Discover our latest collection designed with passion and purpose.', style: { fontSize: '18px', lineHeight: '1.7', opacity: '0.8', marginBottom: '32px' } },
              { type: 'a', content: 'Shop Now', attributes: { href: '/shop' }, style: { display: 'inline-block', padding: '14px 36px', backgroundColor: '#ffffff', color: '#111827', borderRadius: '8px', fontSize: '15px', fontWeight: '600', textDecoration: 'none' } },
            ],
          },
        ],
      },
    },
  },
  {
    id: 'hero-split',
    category: 'Hero',
    label: 'Split Hero',
    description: 'Two-column hero with image and text side by side',
    icon: Layout,
    block: {
      id: 'hero-split',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { display: 'flex', minHeight: '70vh', flexWrap: 'wrap' },
        children: [
          {
            type: 'div',
            style: { flex: '1 1 50%', minWidth: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px 48px' },
            children: [
              { type: 'span', content: 'New Collection', style: { fontSize: '13px', fontWeight: '600', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px', display: 'block' } },
              { type: 'h1', content: 'Elegance Redefined', style: { fontSize: '48px', fontWeight: '700', lineHeight: '1.15', marginBottom: '20px', color: '#111827', letterSpacing: '-0.02em' } },
              { type: 'p', content: 'Explore our curated collection of handcrafted pieces that blend traditional artistry with modern design.', style: { fontSize: '16px', lineHeight: '1.7', color: '#64748b', marginBottom: '32px' } },
              { type: 'a', content: 'Explore Collection', attributes: { href: '/shop' }, style: { display: 'inline-block', padding: '12px 32px', backgroundColor: '#111827', color: '#ffffff', borderRadius: '8px', fontSize: '14px', fontWeight: '600', textDecoration: 'none' } },
            ],
          },
          {
            type: 'div',
            style: { flex: '1 1 50%', minWidth: '300px', minHeight: '400px', backgroundColor: '#f3f4f6', backgroundSize: 'cover', backgroundPosition: 'center' },
            children: [
              { type: 'img', attributes: { src: '/static/images/placeholder.jpg', alt: 'Collection' }, style: { width: '100%', height: '100%', objectFit: 'cover' } },
            ],
          },
        ],
      },
    },
  },
  {
    id: 'features-grid',
    category: 'Content',
    label: 'Feature Grid',
    description: '3-column grid showcasing key features with icons',
    icon: Grid,
    block: {
      id: 'features-grid',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '80px 24px', maxWidth: '1200px', margin: '0 auto' },
        children: [
          { type: 'h2', content: 'Why Choose Us', style: { fontSize: '36px', fontWeight: '700', textAlign: 'center', marginBottom: '48px', color: '#111827', letterSpacing: '-0.02em' } },
          {
            type: 'div',
            style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px' },
            children: [
              { type: 'div', style: { textAlign: 'center', padding: '24px' }, children: [
                { type: 'div', style: { width: '56px', height: '56px', borderRadius: '12px', backgroundColor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px' }, content: '✨' },
                { type: 'h3', content: 'Handcrafted Quality', style: { fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#111827' } },
                { type: 'p', content: 'Every piece is carefully crafted by skilled artisans using premium materials.', style: { fontSize: '14px', lineHeight: '1.6', color: '#64748b' } },
              ]},
              { type: 'div', style: { textAlign: 'center', padding: '24px' }, children: [
                { type: 'div', style: { width: '56px', height: '56px', borderRadius: '12px', backgroundColor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px' }, content: '🚚' },
                { type: 'h3', content: 'Free Shipping', style: { fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#111827' } },
                { type: 'p', content: 'Enjoy free delivery on all orders. We ship across India with care.', style: { fontSize: '14px', lineHeight: '1.6', color: '#64748b' } },
              ]},
              { type: 'div', style: { textAlign: 'center', padding: '24px' }, children: [
                { type: 'div', style: { width: '56px', height: '56px', borderRadius: '12px', backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px' }, content: '💎' },
                { type: 'h3', content: 'Premium Fabrics', style: { fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#111827' } },
                { type: 'p', content: 'Sourced from the finest mills, our fabrics are soft, durable, and beautiful.', style: { fontSize: '14px', lineHeight: '1.6', color: '#64748b' } },
              ]},
            ],
          },
        ],
      },
    },
  },
  {
    id: 'cta-banner',
    category: 'CTA',
    label: 'CTA Banner',
    description: 'Bold call-to-action banner with gradient background',
    icon: Megaphone,
    block: {
      id: 'cta-banner',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '80px 24px', background: 'linear-gradient(135deg, #1e1b4b, #312e81)', textAlign: 'center', color: '#ffffff' },
        children: [
          { type: 'h2', content: 'Ready to Elevate Your Style?', style: { fontSize: '40px', fontWeight: '700', marginBottom: '16px', letterSpacing: '-0.02em' } },
          { type: 'p', content: 'Join thousands of fashion-forward customers. Get 15% off your first order.', style: { fontSize: '18px', opacity: '0.9', marginBottom: '32px', maxWidth: '500px', margin: '0 auto 32px' } },
          { type: 'a', content: 'Start Shopping', attributes: { href: '/shop' }, style: { display: 'inline-block', padding: '14px 40px', backgroundColor: '#ffffff', color: '#1e1b4b', borderRadius: '10px', fontSize: '15px', fontWeight: '600', textDecoration: 'none' } },
        ],
      },
    },
  },
  {
    id: 'testimonials-carousel',
    category: 'Testimonials',
    label: 'Testimonial Cards',
    description: 'Customer testimonials in a card grid layout',
    icon: Star,
    block: {
      id: 'testimonials-carousel',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '80px 24px', backgroundColor: '#f9fafb' },
        children: [
          { type: 'h2', content: 'What Our Customers Say', style: { fontSize: '36px', fontWeight: '700', textAlign: 'center', marginBottom: '48px', color: '#111827' } },
          {
            type: 'div',
            style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', maxWidth: '1100px', margin: '0 auto' },
            children: [1, 2, 3].map((i) => ({
              type: 'div',
              style: { backgroundColor: '#ffffff', padding: '28px', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
              children: [
                { type: 'div', content: '★★★★★', style: { fontSize: '16px', color: '#f59e0b', marginBottom: '12px', letterSpacing: '2px' } },
                { type: 'p', content: `"Absolutely stunning quality! The fabric is luxurious and the fit is perfect. Will definitely order again."`, style: { fontSize: '14px', lineHeight: '1.6', color: '#374151', marginBottom: '16px' } },
                { type: 'p', content: `Customer ${i}`, style: { fontSize: '13px', fontWeight: '600', color: '#111827' } },
              ],
            })),
          },
        ],
      },
    },
  },
  {
    id: 'newsletter-signup',
    category: 'Contact',
    label: 'Newsletter Signup',
    description: 'Email subscription section with input field',
    icon: Mail,
    block: {
      id: 'newsletter-signup',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '60px 24px', textAlign: 'center', backgroundColor: '#111827', color: '#ffffff' },
        children: [
          { type: 'h2', content: 'Stay in the Loop', style: { fontSize: '32px', fontWeight: '700', marginBottom: '12px' } },
          { type: 'p', content: 'Subscribe to get exclusive offers, new arrivals, and styling tips.', style: { fontSize: '16px', opacity: '0.8', marginBottom: '28px' } },
          {
            type: 'div',
            style: { display: 'flex', gap: '12px', justifyContent: 'center', maxWidth: '460px', margin: '0 auto' },
            children: [
              { type: 'div', style: { flex: '1', height: '48px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', padding: '0 16px', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }, content: 'Enter your email' },
              { type: 'a', content: 'Subscribe', attributes: { href: '#' }, style: { display: 'flex', alignItems: 'center', padding: '0 24px', backgroundColor: '#6366f1', color: '#ffffff', borderRadius: '10px', fontSize: '14px', fontWeight: '600', textDecoration: 'none', whiteSpace: 'nowrap' } },
            ],
          },
        ],
      },
    },
  },
  {
    id: 'stats-counter',
    category: 'Stats',
    label: 'Stats Counter',
    description: 'Impressive numbers showcasing brand achievements',
    icon: BarChart3,
    block: {
      id: 'stats-counter',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '60px 24px', backgroundColor: '#ffffff' },
        children: [
          {
            type: 'div',
            style: { display: 'flex', justifyContent: 'center', gap: '64px', flexWrap: 'wrap', maxWidth: '900px', margin: '0 auto' },
            children: [
              { type: 'div', style: { textAlign: 'center' }, children: [
                { type: 'div', content: '10K+', style: { fontSize: '42px', fontWeight: '800', color: '#111827', letterSpacing: '-0.02em' } },
                { type: 'p', content: 'Happy Customers', style: { fontSize: '14px', color: '#6b7280', marginTop: '4px' } },
              ]},
              { type: 'div', style: { textAlign: 'center' }, children: [
                { type: 'div', content: '500+', style: { fontSize: '42px', fontWeight: '800', color: '#111827', letterSpacing: '-0.02em' } },
                { type: 'p', content: 'Products', style: { fontSize: '14px', color: '#6b7280', marginTop: '4px' } },
              ]},
              { type: 'div', style: { textAlign: 'center' }, children: [
                { type: 'div', content: '50+', style: { fontSize: '42px', fontWeight: '800', color: '#111827', letterSpacing: '-0.02em' } },
                { type: 'p', content: 'Collections', style: { fontSize: '14px', color: '#6b7280', marginTop: '4px' } },
              ]},
              { type: 'div', style: { textAlign: 'center' }, children: [
                { type: 'div', content: '4.9', style: { fontSize: '42px', fontWeight: '800', color: '#111827', letterSpacing: '-0.02em' } },
                { type: 'p', content: 'Rating', style: { fontSize: '14px', color: '#6b7280', marginTop: '4px' } },
              ]},
            ],
          },
        ],
      },
    },
  },
  {
    id: 'spacer-divider',
    category: 'Other',
    label: 'Spacer / Divider',
    description: 'Simple spacing section with optional decorative line',
    icon: Layers,
    block: {
      id: 'spacer-divider',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '40px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
        children: [
          { type: 'div', style: { width: '80px', height: '2px', backgroundColor: '#e5e7eb', borderRadius: '1px' }, children: [] },
        ],
      },
    },
  },
];

// ── Helpers ───────────────────────────────────────────────────────────
function getCategoryForBlock(block) {
  const id = (block.id || '').toLowerCase();
  for (const [cat, config] of Object.entries(SECTION_CATEGORIES)) {
    if (cat === 'Other') continue;
    if (config.match.some(m => id.includes(m))) return cat;
  }
  return 'Other';
}

function formatBlockName(block) {
  const id = block.id || block.type || 'section';
  return id.charAt(0).toUpperCase() + id.slice(1).replace(/[-_]/g, ' ');
}

function deepCloneBlock(block) {
  return JSON.parse(JSON.stringify(block));
}

const FAVORITES_KEY = 'vasstr_section_favorites';
const RECENT_KEY = 'vasstr_section_recent';

function loadFavorites() {
  try { return JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]'); } catch { return []; }
}
function saveFavorites(favs) {
  try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs)); } catch {}
}
function loadRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}
function addToRecent(id) {
  try {
    let recent = loadRecent().filter(r => r !== id);
    recent.unshift(id);
    if (recent.length > 10) recent = recent.slice(0, 10);
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
  } catch {}
}

// ── Main Component ───────────────────────────────────────────────────
export default function SectionLibrary({ templateJSON, onAddSection, onClose, pageType }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('templates'); // 'templates' | 'current' | 'favorites'
  const [favorites, setFavorites] = useState(loadFavorites);
  const [addedId, setAddedId] = useState(null);
  const isAboutPage = pageType === 'about';

  const toggleFavorite = useCallback((id) => {
    setFavorites(prev => {
      const next = prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id];
      saveFavorites(next);
      return next;
    });
  }, []);

  // ── About page sections ──
  const aboutCategorized = useMemo(() => {
    if (!isAboutPage) return {};
    const groups = {};
    for (const section of aboutSectionBlocks) {
      const cat = section.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(section);
    }
    return groups;
  }, [isAboutPage]);

  // ── Template sections (generic) ──
  const templateCategorized = useMemo(() => {
    const groups = {};
    const sections = isAboutPage ? [] : GENERIC_SECTION_TEMPLATES;
    for (const section of sections) {
      const cat = section.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(section);
    }
    return groups;
  }, [isAboutPage]);

  // ── Current page sections ──
  const currentCategorized = useMemo(() => {
    if (!templateJSON?.blocks) return {};
    const seen = new Set();
    const groups = {};
    for (const block of templateJSON.blocks) {
      const key = block.id || block.type || ('block-' + Math.random());
      if (seen.has(key)) continue;
      seen.add(key);
      const category = getCategoryForBlock(block);
      if (!groups[category]) groups[category] = [];
      groups[category].push(block);
    }
    return groups;
  }, [templateJSON]);

  // ── Favorite sections ──
  const favoriteSections = useMemo(() => {
    const all = [...GENERIC_SECTION_TEMPLATES];
    if (isAboutPage) {
      aboutSectionBlocks.forEach(s => all.push({ ...s, id: s.block?.id || s.label }));
    }
    return all.filter(s => favorites.includes(s.id || s.block?.id));
  }, [favorites, isAboutPage]);

  // Determine which sections to show based on active tab
  const activeSections = useMemo(() => {
    if (activeTab === 'favorites') {
      return { 'Favorites': favoriteSections };
    }
    if (activeTab === 'current') return currentCategorized;
    if (isAboutPage) return aboutCategorized;
    return templateCategorized;
  }, [activeTab, isAboutPage, aboutCategorized, templateCategorized, currentCategorized, favoriteSections]);

  // ── Filter by search ──
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return activeSections;
    const q = searchQuery.toLowerCase();
    const filtered = {};
    for (const [category, items] of Object.entries(activeSections)) {
      const matching = items.filter(item => {
        const label = (item.label || formatBlockName(item)).toLowerCase();
        const desc = (item.description || '').toLowerCase();
        const id = (item.id || '').toLowerCase();
        return label.includes(q) || desc.includes(q) || id.includes(q) || category.toLowerCase().includes(q);
      });
      if (matching.length > 0) filtered[category] = matching;
    }
    return filtered;
  }, [activeSections, searchQuery]);

  const handleAdd = useCallback((item) => {
    let blockToAdd;
    if (item.block) {
      // Template or about section
      blockToAdd = deepCloneBlock(item.block);
      blockToAdd.id = (item.block.id || 'section') + '-' + Date.now();
    } else {
      // Current section (duplicate)
      blockToAdd = deepCloneBlock(item);
      blockToAdd.id = (item.id || 'section') + '-new-' + Date.now();
    }
    blockToAdd.enabled = true;
    onAddSection(blockToAdd);
    addToRecent(item.id || item.block?.id || 'unknown');

    // Brief visual feedback
    setAddedId(item.id || item.block?.id || Math.random());
    setTimeout(() => setAddedId(null), 1000);
  }, [onAddSection]);

  const totalSections = Object.values(filteredCategories).reduce((sum, arr) => sum + arr.length, 0);

  const TABS = [
    { key: 'templates', label: isAboutPage ? 'About Sections' : 'Ready-Made', icon: Sparkles },
    { key: 'current', label: 'On This Page', icon: Layers },
    { key: 'favorites', label: 'Saved', icon: Bookmark, count: favorites.length },
  ];

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: '#ffffff', borderLeft: '1px solid #e5e7eb',
      width: '320px', minWidth: '320px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', background: '#1e293b', color: '#ffffff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Layers style={{ width: '16px', height: '16px' }} />
          <span style={{ fontSize: '13px', fontWeight: 600 }}>Section Library</span>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
          padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'none'; }}
        >
          <X style={{ width: '16px', height: '16px' }} />
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 8px', flexShrink: 0,
      }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                padding: '9px 8px', border: 'none', borderBottom: isActive ? '2px solid #6366f1' : '2px solid transparent',
                background: 'transparent', cursor: 'pointer',
                fontSize: 11, fontWeight: isActive ? 600 : 500,
                color: isActive ? '#6366f1' : '#6b7280',
                transition: 'all 0.15s',
              }}
            >
              <Icon style={{ width: 12, height: 12 }} />
              {tab.label}
              {tab.count > 0 && (
                <span style={{
                  fontSize: 9, fontWeight: 700, background: isActive ? '#eef2ff' : '#f1f5f9',
                  color: isActive ? '#6366f1' : '#94a3b8', padding: '1px 5px', borderRadius: 4,
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '6px 10px',
        }}>
          <Search style={{ width: '14px', height: '14px', color: '#9ca3af', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search sections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', color: '#374151', width: '100%' }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 0, display: 'flex' }}>
              <X style={{ width: 12, height: 12 }} />
            </button>
          )}
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
          {totalSections} section{totalSections !== 1 ? 's' : ''} available
        </div>
      </div>

      {/* Section List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
        {Object.keys(filteredCategories).length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: '#9ca3af', fontSize: '12px' }}>
            {activeTab === 'favorites' && favorites.length === 0
              ? 'No saved sections yet. Click the bookmark icon on any section to save it for quick access later.'
              : activeTab === 'current' && (!templateJSON?.blocks || templateJSON.blocks.length === 0)
                ? 'This page has no sections yet. Go to the "Ready-Made" tab to add some.'
                : searchQuery
                  ? 'No sections match your search. Try a different keyword.'
                  : 'No sections available.'}
          </div>
        )}

        {Object.entries(filteredCategories).map(([category, items]) => {
          const CategoryIcon = isAboutPage && activeTab === 'templates'
            ? (ABOUT_CATEGORY_ICONS[category] || Layers)
            : (SECTION_CATEGORIES[category]?.icon || Layers);

          return (
            <div key={category} style={{ marginBottom: '16px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 4px', marginBottom: '6px',
              }}>
                <CategoryIcon style={{ width: '12px', height: '12px', color: '#6b7280' }} />
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {category}
                </span>
                <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: 'auto' }}>{items.length}</span>
              </div>

              {items.map((item) => {
                const itemId = item.id || item.block?.id || Math.random();
                const isFav = favorites.includes(itemId);
                const justAdded = addedId === itemId;

                return (
                  <SectionCard
                    key={itemId}
                    label={item.label || formatBlockName(item)}
                    description={item.description || (item.enabled === false ? 'Hidden' : 'Visible')}
                    icon={item.icon || CategoryIcon}
                    isFavorite={isFav}
                    onToggleFavorite={() => toggleFavorite(itemId)}
                    onAdd={() => handleAdd(item)}
                    justAdded={justAdded}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Section Card ─────────────────────────────────────────────────────
function SectionCard({ label, description, icon: Icon, onAdd, isFavorite, onToggleFavorite, justAdded }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '8px 10px', marginBottom: '4px', borderRadius: '8px',
        background: justAdded ? '#ecfdf5' : (hovered ? '#f3f4f6' : '#fafafa'),
        border: '1px solid', borderColor: justAdded ? '#86efac' : (hovered ? '#d1d5db' : '#f3f4f6'),
        cursor: 'pointer', transition: 'all 0.15s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Icon */}
      <div
        style={{
          width: '32px', height: '32px', borderRadius: '6px',
          background: justAdded ? '#dcfce7' : (hovered ? '#e0e7ff' : '#e5e7eb'),
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          transition: 'background 0.15s ease',
        }}
        onClick={onAdd}
      >
        {justAdded ? (
          <CheckCircle style={{ width: '14px', height: '14px', color: '#16a34a' }} />
        ) : (
          <Icon style={{ width: '14px', height: '14px', color: hovered ? '#4f46e5' : '#6b7280', transition: 'color 0.15s ease' }} />
        )}
      </div>

      {/* Label */}
      <div style={{ flex: 1, minWidth: 0 }} onClick={onAdd}>
        <div style={{ fontSize: '12px', fontWeight: 500, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </div>
        <div style={{ fontSize: '10px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {description}
        </div>
      </div>

      {/* Favorite toggle */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
        style={{
          opacity: hovered || isFavorite ? 1 : 0, transition: 'opacity 0.15s ease',
          background: 'none', border: 'none', cursor: 'pointer', padding: 2,
          color: isFavorite ? '#f59e0b' : '#cbd5e1', display: 'flex',
        }}
        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      >
        {isFavorite ? <BookmarkCheck style={{ width: '14px', height: '14px' }} /> : <Bookmark style={{ width: '14px', height: '14px' }} />}
      </button>

      {/* Add button */}
      <div
        onClick={onAdd}
        style={{
          opacity: hovered ? 1 : 0, transition: 'opacity 0.15s ease',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '24px', height: '24px', borderRadius: '6px',
          background: '#6366f1', flexShrink: 0, cursor: 'pointer',
        }}
      >
        <Plus style={{ width: '12px', height: '12px', color: '#ffffff' }} />
      </div>
    </div>
  );
}
